// src/components/Admin/Leads/SalesLeadCategoryPicker.jsx
//
// Multi-select category chip picker for the Sales Call Leads page, built on
// top of the existing CategorySearch autocomplete. Category "group" expansion
// (parent -> children, leaf -> siblings) happens client-side via
// categoryGroupUtils so the backend just receives a flat list of category ids.
import React from 'react';
import { CategorySearch } from '../Category/CategorySearch';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { X } from 'lucide-react';
import { resolveCategorySelection, addCategoryChip, removeCategoryChip } from '../../../utilities/categoryGroupUtils';

export default function SalesLeadCategoryPicker({ locale, categories, chips, onChangeChips, includeGroup, onChangeIncludeGroup }) {
  const handleSelect = (cat) => {
    onChangeChips(addCategoryChip(chips, cat));
  };

  const handleRemove = (categoryId) => {
    onChangeChips(removeCategoryChip(chips, categoryId));
  };

  const resolvedIds = chips.flatMap((cat) => resolveCategorySelection(categories, cat, includeGroup));
  const isEmptyBecauseParentOnly = chips.length > 0 && resolvedIds.length === 0;

  return (
    <div className="flex flex-col gap-2">
      <CategorySearch locale={locale} onSelect={handleSelect} placeholder="Add a category..." />

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((cat) => (
            <Badge key={cat.id} variant="secondary" className="gap-1 pr-1">
              {cat.name}
              <button type="button" onClick={() => handleRemove(cat.id)} aria-label={`Remove ${cat.name}`} className="ml-0.5 rounded-full hover:bg-muted-foreground/20">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer w-fit">
        <Checkbox checked={includeGroup} onCheckedChange={(checked) => onChangeIncludeGroup(Boolean(checked))} />
        Include sibling categories (whole group)
      </label>

      {isEmptyBecauseParentOnly && (
        <p className="text-xs text-amber-600">
          Selected categories have no leads of their own — try turning on "Include sibling categories" to see leads filed under their child categories.
        </p>
      )}
    </div>
  );
}
